import type { ReactNode } from "react";
import { Loader2, ShieldAlert } from "lucide-react";

import { useCarrierMe } from "@/api/carrier";
import type { CarrierMeResponse } from "@/api/carrier";

type CarrierCapability = keyof Pick<
  CarrierMeResponse["capabilities"],
  "canManageCarrierSettings" | "canManageTeam" | "canManagePricing"
>;

const CAPABILITY_LABELS: Record<CarrierCapability, string> = {
  canManageCarrierSettings: "Carrier admin permission required",
  canManageTeam: "Team management permission required",
  canManagePricing: "Pricing permission required",
};

interface RequireCarrierCapabilityProps {
  capability: CarrierCapability;
  children: ReactNode;
}

export function RequireCarrierCapability({
  capability,
  children,
}: RequireCarrierCapabilityProps) {
  const carrierMe = useCarrierMe();

  if (carrierMe.isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <Loader2
          aria-label="Loading carrier permissions"
          className="h-8 w-8 animate-spin text-muted-foreground"
          role="status"
        />
      </div>
    );
  }

  if (!carrierMe.data?.capabilities[capability]) {
    return (
      <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 px-6 text-center">
        <ShieldAlert className="h-8 w-8 text-muted-foreground" />
        <h1 className="text-xl font-semibold">
          {CAPABILITY_LABELS[capability]}
        </h1>
        <p className="max-w-md text-sm text-muted-foreground">
          This account does not have permission to open this page.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
